import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { confirmSignOutWithUnsyncedWork } from "@/lib/unsyncedWorkGuard";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Truck, MapPin, FileText, LogOut, Shield, Settings, Car, BarChart3, Users, CreditCard, Calendar, User, X, Info, PoundSterling, HelpCircle, ClipboardCheck, Calculator, Building2, FileSpreadsheet } from "lucide-react";
import blueLogo from "@/assets/transportmate-logo-blue-transparent.png";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { useJobNotifications } from "@/hooks/useJobNotifications";
import { useNativePushRegistration } from "@/hooks/useNativePushRegistration";
import { usePendingDeepLink } from "@/hooks/usePendingDeepLink";
import { PendingJobsSection } from "@/components/PendingJobsSection";
import { AwaitingDeliverySection } from "@/components/AwaitingDeliverySection";
import { CompletedJobsSection } from "@/components/CompletedJobsSection";


import { NotificationBell } from "@/components/NotificationBell";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsNativeApp } from "@/components/NativeAppLayout";
import { NativeStickyHeader } from "@/components/NativeStickyHeader";
import { isNativePlatform } from "@/hooks/useIsNative";
import { NativeLocationPermission } from "@/components/NativeLocationPermission";
import { Capacitor } from "@capacitor/core";
import { checkLocationPermission } from "@/lib/nativeLocation";
import { DesktopAdminLayout } from "@/components/desktop/DesktopAdminLayout";
import { AppFooter } from "@/components/AppFooter";
import { DesktopAdminDashboard } from "@/components/desktop/DesktopAdminDashboard";
import { SubscriptionConfirmationDialog } from "@/components/SubscriptionConfirmationDialog";
import { BiometricOptInPrompt } from "@/components/BiometricOptInPrompt";
import { DashboardSyncStatus } from "@/components/DashboardSyncStatus";
import { triggerAutoPrefetch } from "@/lib/autoPrefetch";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { JobLimitWarning } from "@/components/JobLimitWarning";
import { PageSkeleton } from "@/components/ui/list-skeleton";
import { SetupCentreCard } from "@/components/onboarding/SetupCentreCard";
import { OnboardingLauncher } from "@/components/onboarding/OnboardingLauncher";
import { getSupportSession } from "@/lib/supportSession";


interface LocationState {
  exitedJob?: {
    vehicle: string;
    status: string;
    appraisalType: string;
  };
}

const Index = () => {
  const [loading, setLoading] = useState(true);
  const [exitBanner, setExitBanner] = useState<LocationState['exitedJob'] | null>(null);
  const [showLocationPerm, setShowLocationPerm] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { markManualLogout } = useAuth();
  const { isAdmin, isDriver, isSuperAdmin, loading: roleLoading } = useUserRole();
  const isMobile = useIsMobile();
  const isNativeApp = useIsNativeApp();
  // Enable real-time notifications for drivers
  useJobNotifications();
  // Register native push notifications (3s after dashboard load)
  useNativePushRegistration();
  // Handle pending deep links from push notification taps
  usePendingDeepLink();

  // Prompt location permission on native after first login
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || loading || roleLoading) return;
    
    // Check if already dismissed in last 24h
    const dismissedAt = localStorage.getItem("location_permission_dismissed_at");
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 24 * 60 * 60 * 1000) return;

    (async () => {
      try {
        const info = await checkLocationPermission();
        if (info.permission !== "granted") {
          setShowLocationPerm(true);
        }
      } catch (e) {
        console.warn("[Dashboard] Location permission check failed:", e);
      }
    })();
  }, [loading, roleLoading]);

  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.exitedJob) {
      setExitBanner(state.exitedJob);
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => setExitBanner(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // ── Auth is handled by ProtectedRoute — DO NOT duplicate here ──
  // Previously this component had its own getSession() + onAuthStateChange listener
  // that would navigate("/auth") if session was null. This RACED with the login flow:
  // after signInWithPassword, the dashboard mounted before the session was fully
  // hydrated in the Supabase client, causing getSession() to return null and
  // navigating back to /auth — producing a white screen.
  //
  // ProtectedRoute already gates all auth checks. This component should only
  // handle dashboard-specific initialization.
  useEffect(() => {
    const initDashboard = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Fire-and-forget: don't block dashboard render on this RPC
          Promise.resolve(supabase.rpc('update_user_last_login', {
            user_uuid: session.user.id
          })).then(({ error }) => {
            if (error) console.error("Error updating last login:", error);
          }).catch(() => {});
        }
      } catch (error) {
        console.error("[Dashboard] Init error:", error);
      } finally {
        setLoading(false);
      }
      // Trigger automatic offline job prefetch (native only, non-blocking)
      triggerAutoPrefetch().catch(() => {});
    };

    initDashboard();
  }, []);

  useEffect(() => {
    // Support Mode: the Super Admin is intentionally acting as a customer —
    // do NOT bounce them back to the Super Admin portal.
    if (!loading && !roleLoading && isSuperAdmin && !getSupportSession()) {
      navigate("/sa", { replace: true });
    }
  }, [loading, roleLoading, isSuperAdmin, navigate]);

  const handleLogout = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: driver } = await supabase
          .from("drivers")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (driver) {
          await supabase
            .from("drivers")
            .update({ is_active: false })
            .eq("id", driver.id);
          console.log('[Logout] Driver marked as inactive');
        }
      }
    } catch (error) {
      console.error('[Logout] Error marking driver as inactive:', error);
    }

    if (!(await confirmSignOutWithUnsyncedWork())) return;
    markManualLogout();
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You've been successfully logged out.",
    });
    navigate("/auth");
  };

  if (!loading && !roleLoading && isSuperAdmin && !getSupportSession()) {
    return null;
  }

  if (loading || roleLoading) {
    return <PageSkeleton className="min-h-screen bg-background" />;
  }

  // Desktop admin layout: non-native, non-mobile, admin role
  const isDesktopAdmin = isAdmin && !isNativeApp && !isNativePlatform() && !isMobile;

  if (isDesktopAdmin) {
    return (
      <>
        <DesktopAdminLayout onLogout={handleLogout}>
          <DesktopAdminDashboard />
        </DesktopAdminLayout>
        <SubscriptionConfirmationDialog />
      </>
    );
  }

  const adminFeatures = [
    // Core actions
    { title: "Create Job", description: "Create a new transport job with customer and vehicle details", icon: Truck, path: "/create-job", gradient: "from-primary to-primary/80" },
    { title: "Vehicle Daily Check", description: "Complete daily walkaround checks for operators license compliance", icon: ClipboardCheck, path: "/vehicle-daily-checks", gradient: "from-orange-500 to-orange-400", mobileOnly: true },
    { title: "Quick Quote", description: "Calculate distance and cost estimates between postcodes", icon: Calculator, path: "/quick-quote", gradient: "from-teal-500 to-teal-400" },
    { title: "Quote Builder", description: "Build detailed quotes with full journey mileage, charges and profit analysis", icon: FileText, path: "/quote-builder", gradient: "from-cyan-500 to-cyan-400" },
    { title: "Schedule", description: "View weekly job schedule and manage driver availability", icon: Calendar, path: "/schedule", gradient: "from-blue-500 to-blue-400" },
    // Analytics & customers
    { title: "Analytics", description: "View comprehensive analytics and performance metrics", icon: BarChart3, path: "/analytics", gradient: "from-emerald-500 to-emerald-400" },
    { title: "Customers", description: "View and manage all customer information", icon: Users, path: "/customers", gradient: "from-indigo-500 to-indigo-400" },
    // Jobs & invoicing
    { title: "Completed Jobs", description: "View all completed jobs and manage invoicing", icon: FileText, path: "/previous-jobs", gradient: "from-secondary to-secondary/80" },
    { title: "Invoices", description: "View and generate invoices for completed jobs", icon: PoundSterling, path: "/invoicing", gradient: "from-amber-500 to-amber-400" },
    { title: "Expenses", description: "View and manage driver expenses and receipts", icon: FileSpreadsheet, path: "/expenses", gradient: "from-amber-500 to-amber-400" },
    // People management
    { title: "Manage Users", description: "Invite users and assign admin or driver roles", icon: Shield, path: "/manage-users", gradient: "from-destructive/70 to-destructive/50" },
    { title: "Driver Management", description: "Track drivers, view GPS locations, and monitor performance", icon: MapPin, path: "/driver-management", gradient: "from-accent/70 to-accent/50" },
    // Fleet & admin
    { title: "Vehicles", description: "Manage vehicle database, specifications and daily checks", icon: Car, path: "/vehicles", gradient: "from-primary/60 to-primary/40" },
    { title: "Subcontractors", description: "Manage subcontractor companies and rate cards", icon: Building2, path: "/subcontractors", gradient: "from-violet-500 to-violet-400" },
    { title: "Subscriptions", description: "Manage user subscriptions, renewals, and free access", icon: CreditCard, path: "/subscriptions", gradient: "from-purple-500 to-purple-400" },
    { title: "Help & Guides", description: "Walkthroughs and tutorials for all platform features", icon: HelpCircle, path: "/help-guides", gradient: "from-cyan-500 to-cyan-400" },
    { title: "Settings", description: "Configure system settings and preferences", icon: Settings, path: "/settings", gradient: "from-muted to-muted/80" },
  ];

  // F-08: Driver Quick Actions are limited to the existing Vehicle Daily Check.
  // Jobs stay in the existing Jobs area / job sections. UI reduction only — not security.
  const driverFeatures: Array<(typeof adminFeatures)[number]> = [
    { title: "Vehicle Daily Check", description: "Complete daily walkaround checks before starting your shift", icon: ClipboardCheck, path: "/vehicle-daily-checks", gradient: "from-orange-500 to-orange-400" },
  ];

  // Fail closed: office actions only for the authenticated admin role,
  // driver actions only for the driver role, nothing while unresolved.
  const baseFeatures = isAdmin
    ? adminFeatures.filter(feature => feature.path !== "/subscriptions")
    : isDriver
      ? driverFeatures
      : [];
  
  const displayFeatures = baseFeatures.filter(feature => 
    !feature.mobileOnly || isMobile
  );

  return (
    <>
      {/* Location permission dialog for native — shown on first dashboard load */}
      <NativeLocationPermission
        open={showLocationPerm}
        onComplete={() => setShowLocationPerm(false)}
      />
      {/* Native sticky header */}
      <NativeStickyHeader>
        <button
          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }}
          className="flex items-center min-w-0 min-h-[40px] bg-transparent border-0 cursor-pointer"
        >
          {logoFailed ? (
            <span className="font-semibold text-foreground">TransportMate</span>
          ) : (
            <img
              src={blueLogo}
              alt="TransportMate"
              className="h-[32px] w-auto max-w-[180px] object-contain"
              onError={() => setLogoFailed(true)}
            />
          )}
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <NotificationBell />
          <Button onClick={handleLogout} variant="ghost" size="sm" className="h-10 w-10 p-0" aria-label="Log out">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </NativeStickyHeader>

      <div className={`min-h-screen ${isNativeApp ? 'bg-background' : 'bg-gradient-to-br from-secondary via-background to-secondary/50'}`}>
        {/* Fixed Mobile Bottom Bar - Dashboard only (web only) */}
        {!isNativeApp && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border px-3 py-2 flex items-center justify-end gap-2 sm:hidden safe-area-bottom">
            <NotificationBell />
            <Button onClick={handleLogout} variant="outline" size="sm" className="shadow-sm text-xs">
              <LogOut className="w-3 h-3 mr-1" />
              Exit
            </Button>
          </div>
        )}
        
        <div className={`mx-auto px-4 ${isNativeApp ? 'native-dashboard-feed pt-5 native-dock-pad' : 'container pt-8 sm:pt-8 py-8 sm:py-12 md:py-16 pb-20 sm:pb-24'}`}>
          {/* Exit Appraisal Banner */}
          {exitBanner && (
            <div className="max-w-4xl mx-auto mb-4 sm:mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="bg-muted/50 border border-border/50 rounded-lg px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-muted-foreground truncate">
                    Exited <span className="font-medium text-foreground">{exitBanner.appraisalType}</span> for{" "}
                    <span className="font-medium text-foreground">{exitBanner.vehicle}</span>
                    <span className="hidden sm:inline">
                      {" · "}
                      <span className="capitalize">{exitBanner.status.replace(/_/g, " ")}</span>
                    </span>
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 flex-shrink-0"
                  onClick={() => setExitBanner(null)}
                  aria-label="Dismiss appraisal notice"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Header - simplified for native */}
          {!isNativeApp && (
            <header className="text-center mb-8 sm:mb-12 md:mb-16 px-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-primary">
                Vehicle Transport Manager
              </h1>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional transport management system for tracking jobs, vehicles, and invoicing
              </p>
            </header>
          )}

          {/* Driver GPS Tracking - now mounted at App root level */}

          {isNativeApp && (
          <section className="native-dashboard-heading-section pb-1 pt-1" aria-labelledby="todays-work-heading">
              <p className="mb-1 text-xs font-bold uppercase text-primary">{isAdmin ? "Live operations" : "Your next task"}</p>
              {/* leading is explicit so the glyph box always fits the line box,
                  independent of any global heading rule (Sora needs >= 1.33em) */}
              <h1 id="todays-work-heading" className="font-heading text-[28px] leading-[1.35] font-bold text-foreground">{isAdmin ? "Today’s Work" : "Ready when you are"}</h1>

              <p className="mt-1 text-sm text-muted-foreground">{isAdmin ? "Dispatch today’s work and resolve what needs attention." : "Continue the active delivery or start your next assigned job."}</p>
            </section>
          )}

          {/* Native-only, event-driven device sync status */}
          {isNativeApp && <DashboardSyncStatus />}

          {/* Job Limit Warning Banner */}
          {isAdmin && (
            <div className={`mx-auto ${isNativeApp ? 'mb-3' : 'max-w-6xl mb-4 sm:mb-6'}`}>
              <JobLimitWarning />
            </div>
          )}

          {/* Office onboarding is intentionally kept out of the driver's field workflow. */}
          {isAdmin && (
            <SectionErrorBoundary sectionName="GettingStarted">
              <div className={`mx-auto ${isNativeApp ? 'mb-3' : 'max-w-6xl mb-4 sm:mb-6 md:mb-8'}`}>
                <SetupCentreCard compact={isNativeApp} />
                <OnboardingLauncher />
              </div>
            </SectionErrorBoundary>
          )}

          {/* Awaiting Delivery Section */}
          <SectionErrorBoundary sectionName="AwaitingDelivery">
            <div className={`mx-auto ${isNativeApp ? 'native-job-section' : 'max-w-6xl mb-4 sm:mb-6 md:mb-8'}`}>
              <AwaitingDeliverySection />
            </div>
          </SectionErrorBoundary>

          {/* Pending Jobs Section */}
          <SectionErrorBoundary sectionName="PendingJobs">
            <div className={`mx-auto ${isNativeApp ? 'native-job-section' : 'max-w-6xl mb-4 sm:mb-6 md:mb-8'}`}>
              <PendingJobsSection />
            </div>
          </SectionErrorBoundary>

          {/* Completed Jobs Section */}
          <SectionErrorBoundary sectionName="CompletedJobs">
            <div className={`mx-auto ${isNativeApp ? 'native-job-section' : 'max-w-6xl mb-4 sm:mb-6 md:mb-8'}`}>
              <CompletedJobsSection />
            </div>
          </SectionErrorBoundary>


          {/* Feature Cards Section */}
          {(!isNativeApp || isAdmin) && displayFeatures.length > 0 && <div className={`mx-auto ${isNativeApp ? 'mb-4' : 'max-w-6xl mb-8 sm:mb-12 md:mb-16'}`}>
            <h2 className={`font-bold mb-3 ${isNativeApp ? 'text-[17px] font-semibold text-primary' : 'text-lg sm:text-xl md:text-2xl text-center mb-4 sm:mb-6'}`}>
              Quick Actions
            </h2>
            <div className={`grid gap-2.5 ${isNativeApp ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 md:gap-6 lg:gap-8'}`}>
              {displayFeatures.map((feature) => (
                <Link key={feature.path} to={feature.path} className="group" aria-label={feature.title}>
                  <Card className={`h-full transition-all duration-300 border-border/50 ${isNativeApp ? 'active:scale-[0.98] bg-card' : 'hover:shadow-xl bg-card/80 backdrop-blur hover:-translate-y-1'}`}>
                    <CardHeader className={isNativeApp ? "p-3" : "p-4 sm:p-6"}>
                      <div className={`flex items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105 ${isNativeApp ? 'mb-2 h-10 w-10 bg-primary/10 text-primary' : `mb-3 h-12 w-12 bg-gradient-to-br ${feature.gradient} text-primary-foreground sm:mb-4 sm:h-14 sm:w-14 sm:rounded-2xl md:h-16 md:w-16`}`}>
                        <feature.icon className={isNativeApp ? 'h-5 w-5' : 'h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8'} />
                      </div>
                      <CardTitle className={isNativeApp ? "text-sm leading-tight" : "text-lg sm:text-xl md:text-2xl"}>
                        {feature.title}
                      </CardTitle>
                      {!isNativeApp && (
                        <CardDescription className="text-sm sm:text-base">
                          {feature.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    {!isNativeApp && (
                      <CardContent className="p-4 sm:p-6 pt-0">
                        <span className="text-sm font-medium text-primary group-hover:text-accent transition-colors">
                          Get Started →
                        </span>
                      </CardContent>
                    )}
                  </Card>
                </Link>
              ))}
            </div>
          </div>}

          {/* Desktop Action Buttons */}
          {!isNativeApp && (
            <div className="fixed bottom-4 sm:bottom-6 right-3 sm:right-6 hidden sm:flex items-center gap-2 z-50">
              <NotificationBell />
              <Button onClick={handleLogout} variant="outline" size="sm" className="shadow-lg text-sm">
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </div>
          )}
          <AppFooter className={isNativeApp ? "pb-[max(1.5rem,env(safe-area-inset-bottom))]" : undefined} />
        </div>
      </div>
      <SubscriptionConfirmationDialog />
      <BiometricOptInPrompt />
    </>
  );
};

export default Index;
